/**
 * Route-level error boundary for App Router.
 * Classifies crashes on critical routes as `blocking` so Sentry Rule 1
 * fires with @channel — at 2 concurrent users, any crash on the canvas
 * or auth flow is a P0. Recovery is a hard reload so leftover tabs pick
 * up the latest client instead of re-rendering the stale bundle.
 */
"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { captureClientError } from "@/lib/sentry/capture-client";
import { Button } from "@/components/atoms/primitives/Button";
import {
  ERROR_BOUNDARY_ACTION_LABEL,
  ERROR_BOUNDARY_COPY,
  ERROR_BOUNDARY_HEADING,
  hardReloadPage,
} from "@/lib/errorBoundaryRecovery";

const CRITICAL_ROUTE_PATTERNS = [
  /^\/[a-z0-9]/,       // /:projectId — project canvas
  /^\/onboarding/,     // /onboarding/* — new user flow
  /^\/sign-in/,        // /sign-in — auth
  /^\/sign-up/,        // /sign-up — auth
];

function isCriticalRoute(pathname: string): boolean {
  return CRITICAL_ROUTE_PATTERNS.some((p) => p.test(pathname));
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
  // reset is intentionally unused — we hard-reload to pick up the latest bundle
  reset: () => void;
}) {
  const pathname = usePathname();

  useEffect(() => {
    captureClientError(error, {
      feature: "error-boundary",
      errorType: "route-crash",
      impact: isCriticalRoute(pathname) ? "blocking" : "degraded",
      contexts: {
        route: { pathname, critical: isCriticalRoute(pathname) },
      },
    });
    console.error("Route error:", error);
  }, [error, pathname]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 text-center">
        <h2 className="text-xl font-semibold text-foreground">
          {ERROR_BOUNDARY_HEADING}
        </h2>
        <p className="text-sm text-muted-foreground">{ERROR_BOUNDARY_COPY}</p>
        <Button type="button" variant="brand" onClick={() => hardReloadPage("route")}>
          {ERROR_BOUNDARY_ACTION_LABEL}
        </Button>
      </div>
    </div>
  );
}
